import { createApiClient } from "~/api/apiClient";
import type { components } from "~/api/generated/api-server-types";
import type { ToolStep } from "./useAgentChat";
import {
  type ConversationProgress,
  replayProgressEvents,
} from "./useConversationProgress";

type StatusResponse = components["schemas"]["ConversationProgress"];

interface ActivityEvent {
  sequence: number;
  event: string;
  data: Record<string, unknown>;
}

export interface ActivityPage {
  activity?:
    | {
        assistant_message_id: string;
        call_id: string;
        duration_ms?: number | null;
        model_turn_number?: number | null;
        events: ActivityEvent[];
      }[]
    | null;
  activity_next_cursor?: Record<string, string> | null;
}

export interface ConversationStatusQuery {
  conversationId: string;
  includeActivity: boolean;
  activityCursor: Record<string, string> | null;
  signal?: AbortSignal;
}

/**
 * One status/activity read. The host picks the route that authorizes it: the
 * caller's own partition by default, or a routine run's viewer authorization.
 * Throws when the read fails.
 */
export type ConversationStatusFetcher = (
  query: ConversationStatusQuery,
) => Promise<ConversationProgress & ActivityPage>;

/** The caller's own partition, through the api-server. */
async function readOwnActivity({
  conversationId,
  activityCursor,
}: ConversationStatusQuery): Promise<ActivityPage> {
  const { data }: { data?: StatusResponse } = await createApiClient().POST(
    "/indy/conversation/status",
    {
      body: {
        conversation_id: conversationId,
        include_activity: true,
        activity_cursor: activityCursor,
      },
    },
  );
  if (!data) throw new Error("Conversation activity unavailable");
  return data;
}

/**
 * A reopened conversation's tool steps, read from the api-server so a history
 * load never cold-starts an Indy Lambda environment beside `/warmup`.
 * Pages may split a message or repeat the latest turn to cover index lag.
 * A host that authorizes the read differently (a routine run) passes its own.
 */
export async function fetchConversationActivity(
  conversationId: string,
  readPage: (
    query: ConversationStatusQuery,
  ) => Promise<ActivityPage> = readOwnActivity,
): Promise<Map<string, ToolStep[]>> {
  const messages = new Map<string, Map<number, ActivityEvent>>();
  let cursor: Record<string, string> | null = null;
  do {
    const page = await readPage({
      conversationId,
      includeActivity: true,
      activityCursor: cursor,
    });
    for (const row of page.activity ?? []) {
      const events = messages.get(row.assistant_message_id) ?? new Map();
      for (const event of row.events) {
        events.set(event.sequence, {
          ...event,
          data: {
            ...event.data,
            duration_ms: row.duration_ms ?? event.data.duration_ms,
            model_turn_number: row.model_turn_number,
          },
        });
      }
      messages.set(row.assistant_message_id, events);
    }
    cursor = page.activity_next_cursor ?? null;
  } while (cursor);
  return new Map(
    [...messages].map(([messageId, events]) => [
      messageId,
      replayProgressEvents(
        [...events.values()].sort((a, b) => a.sequence - b.sequence),
      ).steps,
    ]),
  );
}
